import { Component } from '@theme/component';

export class KuxaroNavigation extends Component {
  requiredRefs = ['dialog', 'trigger'];
  #controller;
  #leaveTimer;
  #keyboard = false;
  #ownsScrollLock = false;

  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.addEventListener('click', this.#click, { signal });
    this.addEventListener('focusin', this.#focus, { signal });
    this.addEventListener('keydown', this.#key, { signal });
    this.refs.dialog.addEventListener('cancel', this.#cancel, { signal });
    this.refs.dialog.addEventListener('close', this.#closed, { signal });
    document.addEventListener('pointermove', this.#pointer, { signal });
    document.documentElement.addEventListener('pointerleave', this.#leave, { signal });
    this.refs.trigger.hidden = false;
    this.setAttribute('data-enhanced', '');
    this.#select(this.querySelector('[data-kx-group][data-active="true"]')?.dataset.kxGroup ?? '1');
  }

  updatedCallback() {
    super.updatedCallback();
    this.setAttribute('data-enhanced', '');
    this.#clear();
    this.refs.trigger.hidden = false;
    this.#select(this.querySelector('[data-kx-group][aria-expanded="true"]')?.dataset.kxGroup ?? '1');
  }

  disconnectedCallback() {
    this.close();
    this.#controller?.abort();
    super.disconnectedCallback();
  }

  open(keyboard = false) {
    this.#clear();
    this.#keyboard = keyboard;
    if (this.refs.dialog.open) return;
    this.#ownsScrollLock = !document.documentElement.hasAttribute('scroll-lock');
    document.documentElement.setAttribute('scroll-lock', '');
    this.refs.dialog.showModal();
    this.refs.trigger.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.#clear();
    if (this.refs.dialog?.open) this.refs.dialog.close();
    this.#closed();
  }

  #closed = () => {
    this.#clear();
    this.refs.trigger?.setAttribute('aria-expanded', 'false');
    if (this.#ownsScrollLock) document.documentElement.removeAttribute('scroll-lock');
    this.#ownsScrollLock = false;
  };

  #clear = () => {
    clearTimeout(this.#leaveTimer);
    this.#leaveTimer = undefined;
  };

  #select(group) {
    for (const row of this.querySelectorAll('[data-kx-group-row]')) {
      row.toggleAttribute('data-selected', row.dataset.kxGroupRow === group);
    }
    for (const button of this.querySelectorAll('button[data-kx-group]')) {
      button.setAttribute('aria-expanded', String(button.dataset.kxGroup === group));
    }
    for (const panel of this.querySelectorAll('[data-kx-panel]')) panel.hidden = panel.dataset.kxPanel !== group;
  }

  #click = (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('[data-kx-open]')) return this.open(event.detail === 0);
    if (event.target.closest('[data-kx-close]')) return this.close();
    const group = event.target.closest('button[data-kx-group]');
    if (group) return this.#select(group.dataset.kxGroup);
    if (event.target.closest('dialog a')) return this.close();
    if (event.target === this.refs.dialog && this.#outside(event)) this.close();
  };

  #focus = (event) => {
    this.#clear();
    const group = event.target.closest?.('[data-kx-group]');
    if (group) this.#select(group.dataset.kxGroup);
  };

  #key = (event) => {
    this.#keyboard = true;
    this.#clear();
    if (event.key === 'Tab' && this.refs.dialog.open) {
      const stops = [...this.refs.dialog.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex="0"]')].filter(node => node.getClientRects().length > 0);
      const first = stops[0];
      const last = stops.at(-1);
      const focused = document.activeElement;
      if (event.shiftKey && (focused === first || focused === this.refs.dialog)) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && (focused === last || focused === this.refs.dialog)) {
        event.preventDefault();
        first?.focus();
      }
    }
    if (event.key === 'Escape' && this.refs.dialog.open) {
      event.preventDefault();
      this.close();
    }
  };

  #cancel = (event) => {
    event.preventDefault();
    this.close();
  };

  #outside(event) {
    const box = this.refs.dialog.getBoundingClientRect();
    return event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom;
  }

  #pointer = (event) => {
    if (event.pointerType !== 'mouse' || !this.refs.dialog.open) return;
    if (this.#outside(event)) return this.#leave(event);
    this.#keyboard = false;
    this.#clear();
    const group = event.target.closest?.('[data-kx-group]');
    if (group) this.#select(group.dataset.kxGroup);
  };

  #leave = (event) => {
    if (event.pointerType !== 'mouse' || this.#keyboard || !this.refs.dialog.open || this.#leaveTimer !== undefined) return;
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    this.#leaveTimer = setTimeout(() => {
      this.#leaveTimer = undefined;
      this.close();
    }, 210);
  };
}

if (!customElements.get('kuxaro-navigation-component')) customElements.define('kuxaro-navigation-component', KuxaroNavigation);
